import { useEffect } from 'react';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { useInternalLayersFeatures } from 'hooks/internalLayerVersion/useListInternalLayerVersionFeaturesQuery';
import { syncInternalLayers } from './syncInternalLayers';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';

/**
 * Syncs internal ArcGIS GraphicsLayer(s) with feature data from PRATUS hosted
 * internal layer versions and manages their lifecycle. Shows one
 * internal_layer_version per internal_layer controlled by the version selected
 * in the sidebar.
 */
export function useInternalLayerSync({
  mapView,
  internalLayerVisibilityById,
  incidentFilterId,
  aorFilterIds,
  selectedInternalLayerVersionByInternalLayerId,
  layersRef,
}: {
  mapView: MapView | null;
  internalLayerVisibilityById: Map<string, boolean>;
  incidentFilterId: string | null;
  aorFilterIds: string[] | null;
  selectedInternalLayerVersionByInternalLayerId: Record<
    string,
    InternalLayerVersion
  >;
  layersRef: React.MutableRefObject<Map<string, GraphicsLayer>>;
}) {
  const { internalLayerVersionsMap, isLoading } = useInternalLayersFeatures(
    incidentFilterId,
    aorFilterIds,
    selectedInternalLayerVersionByInternalLayerId
  );

  useEffect(() => {
    const layers = layersRef.current;

    if (!mapView?.map) return;
    if (internalLayerVersionsMap.size === 0) {
      if (mapView?.map) {
        layers.forEach((layer) => mapView.map.remove(layer));
        layers.clear();
      }
      return;
    }

    syncInternalLayers({
      map: mapView.map,
      internalLayerVersionsMap,
      internalLayerVisibilityById,
      layersRef,
    });

    // Cleanup: Remove all internal layers if this hook unmounts
    return () => {
      if (mapView?.map) {
        layers.forEach((layer) => mapView.map.remove(layer));
        layers.clear();
      }
    };
  }, [mapView, internalLayerVersionsMap, internalLayerVisibilityById]);

  return { isLoading };
}
